import { FormGroup, Validators, ValidatorFn, ValidationErrors, ControlContainer, FormRecord } from '@angular/forms';
import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, AfterContentChecked, ChangeDetectorRef } from '@angular/core';
import { RessourcesEnfantModel, ProprietesRessourceEnfantModel, RessourceEnfantModel, RessourcesEnfantModelAdapter } from './models/ressources-enfant.model';
import { TagWaService } from '../../../services/tag-wa.service';
import { ConstantePage } from '../../../constants/constante-page';
import { CcdRubriqueMontantModel, RubriqueMontantForm } from '@cnaf.usagers.appli/cnaf-commons-dral';
import { ConstantesRessourcesEnfant } from './constants/constante-ressources-enfant';
import { UserService } from '../../../services/user.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-ressources-enfant',
  templateUrl: './ressources-enfant.component.html',
  styleUrls: ['./ressources-enfant.component.css']
})
export class RessourcesEnfantComponent implements OnInit, OnDestroy, AfterContentChecked {
  
  /**
   * Nom du FormGroup du composant dans le form parent
   */
  @Input() name: string;

  /**
   * Index de l'enfant 
   */
  @Input() indexEnfant: number;

  /**
   * Donnée en entrée contenant les données de ressources de l'enfant
   */
  @Input() ressourcesEnfant : RessourcesEnfantModel;

  /** liste des ressources saisie */
  @Output() dataChange = new EventEmitter<RessourcesEnfantModel>();  

  /**
   * Form group du composant
   */
  ressourcesEnfantForm = new FormRecord<FormGroup<RubriqueMontantForm>>({});
  
  /**
   * Type de ressources (propriétés)
   */
  listeConfRessources = new Array<ProprietesRessourceEnfantModel>();

  /**
   * Liste des subscribers
   */
  subscribers = new Array<Subscription>();

  /**
   * Messages d'erreurs à afficher dans les montants
   */
  montantMapErreurs = ConstantesRessourcesEnfant.MAP_ERREURS_MONTANTS;

  /**
   * Constructeur
   * 
   * @param controlContainer 
   * @param cd 
   * @param tagWaService 
   */
  constructor(    
    private readonly controlContainer: ControlContainer,
    private readonly cd: ChangeDetectorRef,
    private readonly tagWaService: TagWaService,
    private readonly userService: UserService) { 
  }

  /**
   * Init du composant : 
   * Ajout dynamique dans le form parent
   * Ajout du validator "aumoinsunecasecochee" sur les ressources
   * Initialisation de l'objet model (si non défini)
   */
  ngOnInit(): void {
    const parentForm = this.controlContainer?.control as FormGroup;
    parentForm.addControl(this.name, this.ressourcesEnfantForm);

    this.ressourcesEnfantForm.setValidators(this.validationCasesACochees());

    const anneeNMoins2 = this.userService.recupererAnneeMoins2AnsYYYY();
    this.listeConfRessources = this.genererConfigRessources(this.indexEnfant.toString(), anneeNMoins2.toString());
    
    if (
      this.ressourcesEnfant === undefined || 
      this.ressourcesEnfant.listeRessources === undefined || 
      this.ressourcesEnfant.listeRessources.length === 0) {
      
      this.ressourcesEnfant = new RessourcesEnfantModel();
      this.ressourcesEnfant.listeRessources = new Array<RessourceEnfantModel>(
        {code : ConstantesRessourcesEnfant.CODE_SALAIRE, rubriqueData : new CcdRubriqueMontantModel()},
        {code : ConstantesRessourcesEnfant.CODE_ALLOCATION_CHOMAGE, rubriqueData : new CcdRubriqueMontantModel()},
        {code : ConstantesRessourcesEnfant.CODE_REVENU_TRAVAILLEUR_INDEPENDANT, rubriqueData : new CcdRubriqueMontantModel()}
      );
    }

    this.subscribers.push(
      this.ressourcesEnfantForm.valueChanges.subscribe(data => {
        this.dataChange.emit(this.ressourcesEnfant);
      })
    );
  }
  
  /**
   * Destruction du composant : 
   * - Suppression du form dans le formGroup parent
   * - Suppression des subscribers
   * - Vidage des données
   */
  ngOnDestroy(): void {
    const parentForm = this.controlContainer?.control as FormGroup;
    parentForm.removeControl(this.name);

    this.subscribers.forEach(sub => sub.unsubscribe());

    this.ressourcesEnfant.listeRessources.forEach( val => {
      val.rubriqueData.isActive = false;
      val.rubriqueData.montant = "";
    });
  }

  /**
   * On force la mise à jour des event dans le formulaire pour éviter les effets de bords et log d'erreur
   */
  ngAfterContentChecked() {
    this.cd.detectChanges();
  }

  /**
   * Generation de la configuration des ressources à afficher en fonction de l'index
   * @param _indexEnfant index de l'enfant
   * @param _anneeMoins2 annee N moins 2 en string
   */
  private genererConfigRessources(_indexEnfant : string, _anneeMoins2 : string) : Array<ProprietesRessourceEnfantModel> {
    return [
      {
        id: ConstantesRessourcesEnfant.CODE_SALAIRE.concat(_indexEnfant),
        code: ConstantesRessourcesEnfant.CODE_SALAIRE,
        libelle: 'Salaires',
        validators: [Validators.required, Validators.pattern(ConstantesRessourcesEnfant.PATTERN_NUMERIQUE_ENTIER_POSITIF)],
        placeHolder: '0'
      },
      {
        id: ConstantesRessourcesEnfant.CODE_ALLOCATION_CHOMAGE.concat(_indexEnfant),
        code: ConstantesRessourcesEnfant.CODE_ALLOCATION_CHOMAGE,
        libelle: 'Allocations de chômage',
        validators: [Validators.required, Validators.pattern(ConstantesRessourcesEnfant.PATTERN_NUMERIQUE_ENTIER_POSITIF)],
        placeHolder: '0'
      },
      {
        id: ConstantesRessourcesEnfant.CODE_REVENU_TRAVAILLEUR_INDEPENDANT.concat(_indexEnfant),
        code: ConstantesRessourcesEnfant.CODE_REVENU_TRAVAILLEUR_INDEPENDANT,
        libelle: 'Revenu ou déficit de travailleur indépendant de l’année '.concat(_anneeMoins2),
        validators: [Validators.required, Validators.pattern(ConstantesRessourcesEnfant.PATTERN_NUMERIQUE_ENTIER)],
        placeHolder: 'Ex : -500, 250'
      }
    ]
  }

  /**
   * Click sur la popup : 
   * TAG WA
   */
  clickPopup() : void {
    this.tagWaService.tagClic(ConstantePage.STATS_CLIC_RESSOURCES_ENFANT, ConstantePage.STATS_PAGE_DECLARATION_ENFANT);  
  }

  /**
   * Validateur custom pour gerer le cas aucune case a cocher
   * Ce validateur renvoie une erreur si aucune case n'est cochée
   */
  validationCasesACochees(): ValidatorFn {
    
    return (controls: FormGroup): ValidationErrors | null => {
      let auMoinsUneCaseCochee = false;
      const ressAdapter = new RessourcesEnfantModelAdapter(this.ressourcesEnfant);
      ressAdapter.mapRessources.forEach(data => {
        auMoinsUneCaseCochee = data.isActive || auMoinsUneCaseCochee;
      });

      const msg = "aucune rubrique active";
      return !auMoinsUneCaseCochee ? {aucuneRubriqueActive: msg} : null;
    };
  
  }
}
